import React, { useCallback, useMemo, useState } from "react";

import Input from "../form-item/Input";
import Modal from "../modal/Modal";

const isValidInviteEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export interface EmailInviteModalProps {
  initialEmail?: string;
  initialUsername?: string;
  onClose: () => void;
  /** OK 時にバリデーション済みのメールと（任意の）ユーザー名を渡す。 */
  // eslint-disable-next-line no-unused-vars
  onConfirm: (email: string, username: string) => void;
}

/**
 * メールアドレス（必須）＋ユーザー名（任意）を入力する招待モーダル。
 *
 * 呼び出し口は 2 つ:
 *  - arc モード: 「メールアドレスで招待」カードを選択した状態で「次へ」を押したとき
 *    （OfferWizardOverlay が開く）。OK でそのまま次ステップへ進む。
 *  - coverflow / list モード: `RecipientStep` の `InvitePicker`（その場でキープ）。
 *
 * 見出し・説明・フッターの 2 ボタンは `Modal` の標準スロット（title / description /
 * primaryAction / secondaryAction）に載せる。幅は `OfferConfirmModal` と同じ
 * `size="default"` に揃え、ウィザード内のモーダルで横幅がばらつかないようにする。
 */
const EmailInviteModal: React.FC<EmailInviteModalProps> = ({
  initialEmail = "",
  initialUsername = "",
  onClose,
  onConfirm,
}) => {
  const [emailDraft, setEmailDraft] = useState<string>(initialEmail);
  const [usernameDraft, setUsernameDraft] = useState<string>(initialUsername);

  const valid = useMemo(() => isValidInviteEmail(emailDraft), [emailDraft]);
  const showError = emailDraft.length > 0 && !valid;

  const handleConfirm = useCallback(() => {
    const trimmedEmail = emailDraft.trim();
    if (!isValidInviteEmail(trimmedEmail)) return;
    onConfirm(trimmedEmail, usernameDraft.trim());
  }, [emailDraft, usernameDraft, onConfirm]);

  return (
    <Modal
      onClose={onClose}
      title="メールアドレスで招待"
      description="未登録のユーザーにも、メール経由でオファーを送れます。"
      size="default"
      primaryAction={{
        label: "この宛先に招待",
        onClick: handleConfirm,
        disabled: !valid,
      }}
      secondaryAction={{ label: "戻る", onClick: onClose }}
    >
      <div className="u-col u-gap16 u-w100">
        <Input
          type="email"
          label="メールアドレス"
          option="required"
          placeholder="example@example.com"
          value={emailDraft}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setEmailDraft(e.target.value)
          }
          error={showError ? "メールアドレスの形式で入力してください" : ""}
        />
        <Input
          type="text"
          label="お相手の表示名"
          option="optional"
          placeholder="後から本人が変更できます"
          value={usernameDraft}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setUsernameDraft(e.target.value)
          }
        />
      </div>
    </Modal>
  );
};

export default EmailInviteModal;
